import type { CreatorListTemplate } from "@unbox/api";
import { BankNote01, HelpCircle, NotificationBox, VideoRecorderOff } from "@untitledui/icons";
import { useState } from "react";
import { type Key, Label } from "react-aria-components";
import { type SubmitHandler, type UseFormReturn, useForm } from "react-hook-form";
import { useCreateCreatorList, useCreatorLists } from "@/api/creator-lists";
import { Tabs } from "@/components/application/tabs/tabs";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Label as FormLabel } from "@/components/base/input/label";
import { RadioButton, RadioGroup } from "@/components/base/radio-buttons/radio-buttons";
import { TextArea } from "@/components/base/textarea/textarea";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { FormField, FormItem } from "@/components/ui/form";
import { MissingCreatorsModal } from "@/pages/creator-lists/missing-creators";
import { error, success } from "@/providers/ToastProvider";
import { cx } from "@/utils/cx";

const tabs = [
  { id: "template", label: "Start with template" },
  { id: "list", label: "Add custom list" },
  { id: "blank", label: "Create with blank list" },
];

type FormValues = { name: string; creatorIds: string; description: string };

export const CreateCreatorList = ({ onCreate }: { onCreate: (id: string) => void }) => {
  const [tab, setTab] = useState<"list" | "blank" | "template">("template");
  const { refetch } = useCreatorLists({ offset: 0, limit: 50 }, "", { column: "createdAt", direction: "asc" });
  const form = useForm<FormValues>({
    defaultValues: { creatorIds: "" },
  });

  const handleCreate = async (id: string) => {
    await refetch();
    onCreate(id);
  };

  return (
    <div className="flex flex-col items-center w-full pb-4 bg-secondary h-full pt-20">
      <Tabs
        selectedKey={tab as Key}
        onSelectionChange={(key) => setTab(key as "list" | "blank" | "template")}
        className="w-[580px] max-md:hidden"
      >
        <Tabs.List className="w-full" type="button-border" items={tabs}>
          {(tab) => <Tabs.Item {...tab} className="flex-grow" />}
        </Tabs.List>
      </Tabs>
      {tab === "template" && <TemplateForm form={form} onCreate={handleCreate} />}
      {tab === "list" && <UploadListForm form={form} onCreate={handleCreate} />}
      {tab === "blank" && <BlankListForm form={form} onCreate={handleCreate} />}
    </div>
  );
};

const getNumberOfIds = (ids: string): number => {
  return parseIds(ids).length;
};

const parseIds = (ids: string): string[] => {
  const byNewline = ids.replaceAll("@", "").split("\n").filter(Boolean);
  // Separated by commas
  if (byNewline.length === 1) return byNewline[0].split(",").filter(Boolean);
  return byNewline;
};

const UploadListForm = ({ onCreate, form }: { onCreate: (id: string) => void; form: UseFormReturn<FormValues> }) => {
  const { mutateAsync, isPending } = useCreateCreatorList();

  const onSubmit: SubmitHandler<{ name: string; creatorIds: string; description: string }> = async (payload) => {
    const creatorIds = parseIds(payload.creatorIds);
    try {
      const res = await mutateAsync({ ...payload, creatorIds });
      if (res.failedCreatorIds.length > 0) {
        setMissingCreators({ listId: res.id, missingCreators: res.failedCreatorIds });
      } else {
        success("Creator List created successfully");
        onCreate(res.id);
      }
    } catch (err) {
      error("Failed to create Creator List");
      throw err;
    }
  };
  const idsCount = getNumberOfIds(form.watch("creatorIds"));
  const [missingCreators, setMissingCreators] = useState<{ listId: string; missingCreators: string[] }>();

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="w-full md:w-[580px]">
      {missingCreators && (
        <MissingCreatorsModal
          creatorIds={missingCreators.missingCreators}
          onClose={() => onCreate(missingCreators.listId)}
        />
      )}
      <div className="relative flex flex-col gap-4 sm:gap-5 py-4">
        <FormField
          control={form.control}
          name="creatorIds"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <TextArea
                isRequired
                label="Creator IDs"
                maxLength={255}
                placeholder={`Paste creator ids here e.g
@creator1
@creator2`}
                {...field}
                rows={8}
              />
              <Label className="text-sm italic">
                Creator ids should each be on a new line or separated by commas. @ symbols are optional.
              </Label>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <Input
                autoFocus
                isRequired
                label="Name"
                maxLength={80}
                placeholder="Creator List Name"
                {...field}
              ></Input>
              <span className="text-xs">Max 80 characters</span>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <TextArea
                maxLength={200}
                label="Description"
                placeholder="Creator List Description"
                {...field}
              ></TextArea>
              <span className="text-xs">Max 200 characters</span>
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-row items-center justify-end gap-2">
        {idsCount > 0 && <Badge>Importing {idsCount} Creators</Badge>}
        <Button color="primary" size="lg" type="submit" isDisabled={isPending || idsCount === 0}>
          Create
        </Button>
      </div>
    </form>
  );
};

const BlankListForm = ({ onCreate, form }: { onCreate: (id: string) => void; form: UseFormReturn<FormValues> }) => {
  const { mutateAsync, isPending } = useCreateCreatorList();

  const onSubmit: SubmitHandler<{ name: string; description: string }> = async (payload) => {
    try {
      const res = await mutateAsync({ name: payload.name, description: payload.description });
      success("Creator List created successfully");
      onCreate(res.id);
    } catch (err) {
      error("Failed to create Creator List");
      throw err;
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="w-full md:w-[580px]">
      <div className="relative flex flex-col gap-4 sm:gap-5 py-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <Input
                autoFocus
                isRequired
                label="Name"
                maxLength={80}
                placeholder="Creator List Name"
                {...field}
              ></Input>
              <span className="text-xs">Max 80 characters</span>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <TextArea
                maxLength={200}
                rows={2}
                label="Description"
                placeholder="Creator List Description"
                {...field}
              ></TextArea>
              <span className="text-xs">Max 200 characters</span>
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-row items-center justify-end gap-2">
        <Button color="primary" size="lg" type="submit" isDisabled={isPending}>
          Create
        </Button>
      </div>
    </form>
  );
};

const TemplateForm = ({ onCreate, form }: { onCreate: (id: string) => void; form: UseFormReturn<FormValues> }) => {
  const { mutateAsync, isPending } = useCreateCreatorList();
  const [template, setTemplate] = useState<CreatorListTemplate>("creators-driving-gmv");

  const onSubmit: SubmitHandler<{ name: string; description: string }> = async (payload) => {
    try {
      const res = await mutateAsync({
        name: payload.name,
        description: payload.description,
        template,
      });
      success("Creator List created successfully");
      onCreate(res.id);
    } catch (err) {
      error("Failed to create Creator List");
      throw err;
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="w-full md:w-[580px]">
      <div className="relative flex flex-col gap-4 sm:gap-5 py-4">
        <div className="flex flex-row items-center justify-between -mb-4">
          <FormLabel isRequired className="">
            Select template
          </FormLabel>
          <Tooltip title={"Select a predefined list template to quickly create a new list"}>
            <TooltipTrigger className="group relative flex cursor-pointer flex-col items-center gap-2 text-fg-quaternary transition duration-100 ease-linear hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover">
              <HelpCircle className="size-4" />
            </TooltipTrigger>
          </Tooltip>
        </div>
        <RadioGroup
          value={template}
          onChange={(value: string) => setTemplate(value as CreatorListTemplate)}
          className="grid grid-cols-3 gap-0"
        >
          <button
            type="button"
            className={cx(
              "flex gap-4 m-2 p-4 flex-col justify-between cursor-pointer rounded-2xl border-secondary bg-primary border-solid border-2 transition-all",
              template === "creators-driving-gmv" && "border-brand",
            )}
            onClick={() => setTemplate("creators-driving-gmv")}
          >
            <div className="mb-2 flex flex-row justify-between">
              <div className="p-2 border-primary border-solid border-[1px] rounded-lg">
                <BankNote01 className="w-5 h-5" />
              </div>
              <RadioButton value="creators-driving-gmv" />
            </div>
            <Label className="font-medium text-left">Creators driving $3k or above in the last 30 days</Label>
          </button>

          <button
            type="button"
            className={cx(
              "flex gap-4 m-2 p-4 flex-col justify-between cursor-pointer rounded-2xl border-secondary bg-primary border-solid border-2 transition-all",
              template === "creators-driving-gmv-with-new-videos" && " border-brand",
            )}
            onClick={() => setTemplate("creators-driving-gmv-with-new-videos")}
          >
            <div className="mb-2 flex flex-row justify-between">
              <div className="p-2 border-primary border-solid border-[1px] rounded-lg">
                <NotificationBox className="w-5 h-5" />
              </div>
              <RadioButton value="creators-driving-gmv-with-new-videos" />
            </div>
            <Label className="font-medium text-left">
              Creators driving over $1k with new content in the last 30 days
            </Label>
          </button>

          <button
            type="button"
            className={cx(
              "flex gap-4 m-2 p-4 flex-col justify-between cursor-pointer rounded-2xl border-secondary bg-primary border-solid border-2 transition-all",
              template === "creators-with-no-new-videos" && " border-brand",
            )}
            onClick={() => setTemplate("creators-with-no-new-videos")}
          >
            <div className="mb-2 flex flex-row justify-between">
              <div className="p-2 border-primary border-solid border-[1px] rounded-lg">
                <VideoRecorderOff className="w-5 h-5" />
              </div>
              <RadioButton value="creators-with-no-new-videos" />
            </div>
            <Label className="font-medium text-left">
              Top performing creators who have not posted in the last 30 days
            </Label>
          </button>
        </RadioGroup>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <Input
                autoFocus
                isRequired
                label="Name"
                maxLength={80}
                placeholder="Creator List Name"
                {...field}
              ></Input>
              <span className="text-xs">Max 80 characters</span>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <TextArea
                maxLength={200}
                rows={2}
                label="Description"
                placeholder="Creator List Description"
                {...field}
              ></TextArea>
              <span className="text-xs">Max 200 characters</span>
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-row items-center justify-end gap-2">
        <Button color="primary" size="lg" type="submit" isDisabled={isPending}>
          Create
        </Button>
      </div>
    </form>
  );
};
